import gql from 'graphql-tag';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getApolloClient, getApolloCache } from '@confluence/graphql';

import {
	EDITOR_ONBOARDING_STATE_KEYS,
	ONBOARDING_TUTORIAL_LABEL,
} from '../../constants/onboarding-state/onboarding-editor-constants';

import { getOnboardingStateGQL, setOnboardingStateGQL } from './OnboardingStatus.graphql';
import { LabelsQuery } from './LabelsQuery.graphql';
import type {
	OnboardingEditorLabelsQuery as OnboardingLabelsQueryType,
	OnboardingEditorLabelsQueryVariables,
} from './__types__/LabelsQuery';
import type {
	GetOnboardingState,
	GetOnboardingStateVariables,
} from './__types__/getOnboardingStateGQL';
import { deserializeState } from './onboardingState';

export const setHasUserPublished = () => {
	void getApolloClient().mutate({
		mutation: setOnboardingStateGQL,
		variables: {
			key: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
			value: 'true',
		},
		optimisticResponse: {
			onboardingState: [
				{
					key: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
					value: 'true',
					__typename: 'OnboardingState',
				},
			],
		},
		update(cache) {
			cache.writeFragment({
				id: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
				fragment: gql`
					fragment myKey on OnboardingState {
						key
						value
					}
				`,
				data: {
					key: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
					value: 'true',
					__typename: 'OnboardingState',
				},
			});
		},
	});
};

const queryVariables = {
	key: Object.values(EDITOR_ONBOARDING_STATE_KEYS),
};

export const getEditorOnboardingQuery = (): void => {
	void getApolloClient().query({
		query: getOnboardingStateGQL,
		variables: queryVariables,
	});
};

export const getEditorOnboardingCache = () => {
	try {
		const cachedEditorOnboardingData = getApolloCache().readQuery<
			GetOnboardingState,
			GetOnboardingStateVariables
		>({
			query: getOnboardingStateGQL,
			variables: queryVariables,
		});
		const state = deserializeState(cachedEditorOnboardingData);

		return state;
	} catch (cacheError) {
		// fire analytics
		void getAnalyticsWebClient().then((analyticsWebClient) => {
			analyticsWebClient.sendOperationalEvent({
				source: 'OnboardingState',
				actionSubject: 'getEditorOnboardingCache',
				action: 'failed',
				attributes: {
					error: cacheError,
				},
			});
		});

		// if cache is empty then call query to load cache
		void getEditorOnboardingQuery();

		// return default state on error
		return {};
	}
};

export const getLabelsQuery = (contentId: string): void => {
	void getApolloClient().query<OnboardingLabelsQueryType, OnboardingEditorLabelsQueryVariables>({
		query: LabelsQuery,
		variables: {
			contentId,
			status: ['current', 'draft'],
		},
	});
};

export const getCachedIsOnboardingEditorLabel = (contentId: string) => {
	try {
		const cachedLabelsQueryData: OnboardingLabelsQueryType | null = getApolloCache().readQuery<
			OnboardingLabelsQueryType,
			OnboardingEditorLabelsQueryVariables
		>({
			query: LabelsQuery,
			variables: {
				contentId,
				status: ['current', 'draft'],
			},
		});

		const labels = cachedLabelsQueryData?.content?.nodes?.[0]?.labels?.nodes || [];

		// purposely exclude creator onboarding label as this is used for hiding the publish flag
		return labels && Boolean(labels.find((label) => label?.label === ONBOARDING_TUTORIAL_LABEL));
	} catch (cacheError) {
		// fire analytics
		void getAnalyticsWebClient().then((analyticsWebClient) => {
			analyticsWebClient.sendOperationalEvent({
				source: 'LabelsQuery',
				actionSubject: 'getEditorLabelsQueryCache',
				action: 'failed',
				attributes: {
					error: cacheError,
				},
			});
		});

		// if cache is empty then call query to load cache
		void getLabelsQuery(contentId);

		// return false on error
		return false;
	}
};
